import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock, Mail, ShieldCheck, User } from "lucide-react";
import { PasswordStrength, isStrongPassword } from "@/components/portfolio/PasswordStrength";
import { PageLoader } from "@/components/portfolio/PageLoader";
import { API, formatApiErrorDetail } from "@/lib/api";

const GoogleButton = () => (
  <button
    data-testid="auth-google-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/conta";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="flex w-full items-center justify-center gap-3 rounded-full border border-[#2A2A33] bg-[#101014] px-6 py-3.5 text-sm font-semibold text-zinc-200 transition-all duration-300 hover:border-[#E50914]/60 hover:text-white"
  >
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 2.02 6.93l3.66 2.84C6.55 7.1 8.99 5.04 12 5.04z" />
      <path fill="#4285F4" d="M23.25 12.26c0-.87-.08-1.5-.25-2.16H12v4.09h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.88 3.63-8.49z" />
      <path fill="#FBBC05" d="M5.69 14.23a6.9 6.9 0 0 1 0-4.46L2.02 6.93a11.26 11.26 0 0 0 0 10.14l3.67-2.84z" />
      <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.73l-3.57-2.77c-.95.66-2.24 1.12-3.89 1.12-3.01 0-5.45-2.05-6.32-4.64l-3.66 2.84c1.82 3.66 5.6 6.18 9.98 6.18z" />
    </svg>
    Continuar com Google
  </button>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => navigate(u.role === "admin" ? "/admin" : "/conta", { replace: true }))
      .catch(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !isStrongPassword(password)) {
      setError("Crie uma senha com pelo menos 8 caracteres, incluindo letras e números.");
      return;
    }
    setLoading(true);
    try {
      const body = mode === "login" ? { email, password } : { name, email, password };
      const res = await fetch(`${API}/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(formatApiErrorDetail(data.detail, "Erro ao entrar"));
      }
      navigate(data.role === "admin" ? "/admin" : "/conta");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-4 py-16 text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-10 h-[480px] w-[480px] rounded-full opacity-20 blur-[140px]"
        style={{ background: "radial-gradient(circle, #E50914 0%, transparent 65%)" }}
      />
      {checking ? (
        <PageLoader testid="auth-loading" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="gradient-ring relative z-10 w-full max-w-md rounded-2xl p-8 shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center justify-between">
            <a href="/" className="font-display text-lg font-extrabold tracking-tight" data-testid="auth-logo">
              LUIZ<span className="text-[#E50914]">.DEV</span>
            </a>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#22222A] bg-[#050505] px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-zinc-500">
              <ShieldCheck className="h-3 w-3 text-emerald-400" />
              Área protegida
            </span>
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold">Área do cliente</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Entre ou crie sua conta para acompanhar suas conversas com o assistente e futuros projetos.
          </p>

          <div className="mt-8 grid grid-cols-2 rounded-full border border-[#22222A] bg-[#050505] p-1">
            <button
              data-testid="auth-tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`rounded-full py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest transition-colors ${
                mode === "login" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              Entrar
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`rounded-full py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest transition-colors ${
                mode === "register" ? "bg-[#E50914] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              Criar conta
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div className="relative">
                <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  required
                  maxLength={80}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                maxLength={254}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu e-mail"
                className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
              />
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
              <input
                data-testid="auth-password-input"
                type="password"
                required
                maxLength={128}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Crie uma senha forte" : "Sua senha"}
                className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
              />
            </div>
            {mode === "register" && <PasswordStrength password={password} />}
            {mode === "login" && (
              <div className="text-right">
                <a
                  data-testid="auth-forgot-link"
                  href="/recuperar-senha"
                  className="text-xs text-zinc-500 transition-colors hover:text-[#E50914]"
                >
                  Esqueci minha senha
                </a>
              </div>
            )}
            {error && (
              <div data-testid="auth-error" className="rounded-xl border border-[#E50914]/40 bg-[#E50914]/10 px-4 py-3 text-sm text-[#FF2A3B]">
                {error}
              </div>
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-red-glow flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E50914] font-mono-code text-sm font-bold uppercase tracking-widest text-white disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Entrar" : "Criar minha conta"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-[#22222A]" />
            <span className="font-mono-code text-[10px] uppercase tracking-widest text-zinc-600">ou</span>
            <span className="h-px flex-1 bg-[#22222A]" />
          </div>

          <GoogleButton />

          <a
            data-testid="auth-back-to-site"
            href="/"
            className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 transition-colors hover:text-[#E50914]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar ao site
          </a>
        </motion.div>
      )}
    </div>
  );
}
